// SupportPage — /support.
//
// Mirrors the simpler "contact us" surface of legacy
// `tricivo_system/operations/support_center.php`. The legacy page wraps a
// full ticket-tracking system (open / mine / all tabs, status changes,
// per-ticket notes); none of those endpoints are exposed by the new server
// yet. This page provides the static support information + a contact form
// that opens the user's default mail client (per the brief: "mailto:
// fallback if no endpoint exists").
//
// When the support endpoint lands server-side, swap the `mailto:` href for
// a POST mutation — the form fields already match.

import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Send } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { Input } from '../../components/form/Input';
import { Textarea } from '../../components/form/Textarea';
import { Select } from '../../components/form/Select';
import { useAuthStore } from '../../store/authStore';
import styles from './SupportPage.module.css';

const SUPPORT_EMAIL = 'support@tricivo.com';

const TYPES = [
  { value: 'general', label: 'General question' },
  { value: 'bug', label: 'Bug report' },
  { value: 'feature_request', label: 'Feature request' },
  { value: 'billing', label: 'Billing / subscription' },
  { value: 'urgent', label: 'Urgent — service issue' },
] as const;

const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
] as const;

interface TicketForm {
  title: string;
  description: string;
  type: string;
  priority: string;
}

const EMPTY: TicketForm = {
  title: '',
  description: '',
  type: 'general',
  priority: 'normal',
};

function buildMailtoHref(form: TicketForm, fromName: string, fromEmail: string): string {
  const subject = `[${form.type.toUpperCase()} | ${form.priority}] ${form.title}`;
  const lines = [
    `From: ${fromName} <${fromEmail}>`,
    `Type: ${form.type}`,
    `Priority: ${form.priority}`,
    '',
    form.description,
  ];
  const body = lines.join('\n');
  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    subject,
  )}&body=${encodeURIComponent(body)}`;
}

export function SupportPage(): JSX.Element {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [form, setForm] = useState<TicketForm>(EMPTY);
  const [submitted, setSubmitted] = useState(false);

  function setField<K extends keyof TicketForm>(key: K, value: TicketForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) return;
    const href = buildMailtoHref(
      form,
      user?.name ?? 'Anonymous',
      user?.email ?? 'unknown@example.com',
    );
    if (typeof window !== 'undefined') {
      window.location.href = href;
    }
    setSubmitted(true);
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Support Centre"
        subtitle="Get help, report a bug, or request a feature"
        actions={
          <Button
            variant="ghost"
            icon={ArrowLeft}
            onClick={() => navigate('/collections/dashboard')}
          >
            Back
          </Button>
        }
      />

      <Card title="Contact us">
        <p className={styles.intro}>
          The fastest way to reach us is via email. The form below opens your
          default mail client with a pre-filled message addressed to{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} className={styles.link}>
            {SUPPORT_EMAIL}
          </a>
          . Replies usually land in your inbox within one business day.
        </p>
        <ul className={styles.contactList}>
          <li>
            <Mail size={16} aria-hidden />
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
          </li>
        </ul>
      </Card>

      <Card title="Open a support request">
        {submitted ? (
          <div className={styles.success}>
            <strong>Message ready to send.</strong>
            <p>
              Your mail client should have just opened with the message
              pre-filled. If nothing happened, copy the details below and email
              us at{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className={styles.link}>
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
            <Button
              variant="secondary"
              onClick={() => {
                setSubmitted(false);
                setForm(EMPTY);
              }}
            >
              Send another
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className={styles.form}>
            <Input
              label="Title"
              value={form.title}
              onChange={(e) => setField('title', e.target.value)}
              required
              placeholder="Short summary of the issue"
            />
            <div className={styles.row}>
              <Select
                label="Type"
                value={form.type}
                onChange={(e) => setField('type', e.target.value)}
                options={TYPES.map((t) => ({ value: t.value, label: t.label }))}
              />
              <Select
                label="Priority"
                value={form.priority}
                onChange={(e) => setField('priority', e.target.value)}
                options={PRIORITIES.map((p) => ({ value: p.value, label: p.label }))}
              />
            </div>
            <Textarea
              label="Description"
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
              required
              rows={6}
              placeholder="Please include steps to reproduce, what you expected, and what actually happened."
            />
            <div className={styles.formActions}>
              <Button type="submit" variant="primary" icon={Send}>
                Send to support
              </Button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
}
